import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { motion } from "framer-motion";
import {
  Building2, Plus, ArrowRightLeft, Info, CheckCircle2, Loader2, LayoutDashboard
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const MultiTenancy = () => {
  const { token, user } = useAuth();
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const [orgs, setOrgs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDomain, setNewDomain] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [oRes, dRes] = await Promise.all([
        axios.get(`${API}/tenants/managed-orgs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tenants/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrgs(oRes.data || []);
      setDashboard(dRes.data || { total_orgs: 0, organizations: [] });
    } catch (error) {
      console.error("Failed:", error);
      toast.error(lang === "es" ? "Error al cargar organizaciones" : "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, [token, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createOrg = async () => {
    if (!newName.trim()) {
      toast.error(lang === "es" ? "El nombre es obligatorio" : "Name is required");
      return;
    }
    setCreating(true);
    try {
      const qs = `name=${encodeURIComponent(newName)}${newDomain ? `&domain=${encodeURIComponent(newDomain)}` : ""}`;
      await axios.post(`${API}/tenants/create-org?${qs}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "es" ? "Organizacion cliente creada" : "Client organization created");
      setShowCreate(false); setNewName(""); setNewDomain("");
      fetchData();
    } catch (error) {
      toast.error(extractError(error, lang === "es" ? "Error" : "Failed"));
    } finally { setCreating(false); }
  };

  const switchOrg = async (orgId, orgName) => {
    if (!(await confirm({
      title: t("mt.switch_title"),
      description: `${t("mt.switch_desc_prefix")} "${orgName}"${t("mt.switch_desc_suffix")}`,
    }))) return;
    try {
      await axios.put(`${API}/tenants/switch/${orgId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === "es" ? "Organizacion cambiada. Recargando..." : "Organization switched. Reloading...");
      setTimeout(() => window.location.reload(), 1000);
    } catch {
      toast.error(lang === "es" ? "Error al cambiar" : "Failed to switch");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>;
  }

  const currentOrgId = user?.organization_id;

  return (
    <div className="p-6 space-y-6" data-testid="multi-tenancy-page">
      <PageHeader
        title={t("tenant.title")}
        description={t("tenant.subtitle")}
        actions={
          <Button onClick={() => setShowCreate(true)} data-testid="create-org-btn">
            <Plus className="w-4 h-4 mr-2" />{t("tenant.new_org")}
          </Button>
        }
      />

      {/* Info banner */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-4 flex gap-3">
          <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-medium">
              {lang === "es" ? "Gestion de Clientes (Multi-Tenant)" : "Client Management (Multi-Tenant)"}
            </p>
            <p className="text-muted-foreground">
              {lang === "es"
                ? "Crea una organizacion por cada cliente. Cada tenant tiene sus propios riesgos, controles, SoA, evidencias e incidentes totalmente aislados. Los proyectos GAP, evaluaciones de riesgo y assessments que ejecutes quedaran asociados al tenant del cliente."
                : "Create one organization per client. Each tenant has its own fully isolated risks, controls, SoA, evidence and incidents. GAP projects, risk assessments and any work you run will be stored under the client's tenant."}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-3xl font-heading font-bold">{dashboard?.total_orgs || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("tenant.managed")}</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-3xl font-heading font-bold text-red-500">
            {(dashboard?.organizations || []).reduce((s, o) => s + (o.risks_critical || 0), 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {lang === "es" ? "Riesgos Criticos (consolidado)" : "Critical Risks (consolidated)"}
          </p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-3xl font-heading font-bold text-orange-500">
            {(dashboard?.organizations || []).reduce((s, o) => s + (o.incidents_open || 0), 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {lang === "es" ? "Incidentes Abiertos (consolidado)" : "Open Incidents (consolidated)"}
          </p>
        </CardContent></Card>
      </div>

      {/* Org Cards */}
      {(dashboard?.organizations || []).length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center space-y-3">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground">
              {lang === "es"
                ? "Todavia no has creado organizaciones cliente. Crea la primera para empezar a gestionar tus clientes."
                : "You haven't created client organizations yet. Create the first one to start managing clients."}
            </p>
            <Button onClick={() => setShowCreate(true)} data-testid="create-first-org-btn">
              <Plus className="w-4 h-4 mr-2" />{t("tenant.new_org")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(dashboard?.organizations || []).map(org => {
            const isCurrent = org.organization_id === currentOrgId;
            return (
              <motion.div key={org.organization_id} whileHover={{ scale: 1.02 }}>
                <Card className={`border-border ${isCurrent ? "ring-2 ring-primary" : ""}`} data-testid={`org-card-${org.organization_id}`}>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-bold truncate">{org.name}</h3>
                          {isCurrent && <Badge variant="default" className="text-[10px]">
                            {lang === "es" ? "ACTUAL" : "CURRENT"}
                          </Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{org.organization_id}</p>
                      </div>
                      <div className={`text-2xl font-bold ${org.health_score >= 70 ? 'text-green-500' : org.health_score >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
                        {org.health_score}
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>{t("tenant.health")}</span><span>{org.health_score}/100</span>
                      </div>
                      <Progress value={org.health_score} className="h-2" />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div><p className="font-bold">{org.risks_total}</p><p className="text-muted-foreground">{lang === "es" ? "Riesgos" : "Risks"}</p></div>
                      <div><p className="font-bold text-red-500">{org.risks_critical}</p><p className="text-muted-foreground">{t("critical")}</p></div>
                      <div><p className="font-bold text-orange-500">{org.incidents_open}</p><p className="text-muted-foreground">{lang === "es" ? "Incidentes" : "Incidents"}</p></div>
                    </div>
                    {isCurrent ? (
                      <Button variant="outline" size="sm" className="w-full" disabled>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {lang === "es" ? "Tenant activo" : "Active tenant"}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="w-full" onClick={() => switchOrg(org.organization_id, org.name)}
                        data-testid={`switch-org-${org.organization_id}`}>
                        <ArrowRightLeft className="w-4 h-4 mr-2" />{t("tenant.switch")}
                      </Button>
                    )}
                    <Link to={`/clients/${org.organization_id}`}>
                      <Button variant="default" size="sm" className="w-full" data-testid={`view-client-${org.organization_id}`}>
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        {lang === "es" ? "Ficha del Cliente" : "Client Dashboard"}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tenant.new_org")}</DialogTitle>
            <DialogDescription>
              {lang === "es"
                ? "Crea una organizacion cliente aislada. Podras asociarla a proyectos GAP, riesgos, etc."
                : "Create an isolated client organization to associate with GAP projects, risks, etc."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t("name")} *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Cliente Corp S.A." data-testid="org-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t("tenant.domain")}</Label>
              <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="cliente.com" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>{t("cancel")}</Button>
            <Button onClick={createOrg} disabled={creating} data-testid="save-org-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("loading")}</> : t("create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MultiTenancy;
